import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, Analytics, logEvent as firebaseLogEvent, setUserProperties } from 'firebase/analytics';
import { isDevelopment } from '../../config/env';

const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'transformation-office';
const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
const appId = process.env.REACT_APP_FIREBASE_APP_ID;
const measurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

const firebaseConfig = apiKey && appId
  ? {
      apiKey,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || `${PROJECT_ID}.firebaseapp.com`,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || `${PROJECT_ID}.appspot.com`,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
      appId,
      ...(measurementId ? { measurementId } : {}),
    }
  : null;

let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;

export type SuperCalculatorKey =
  | 'purchase_planner'
  | 'affordability'
  | 'mortgage_loan'
  | 'profile_manager'
  | 'buying_calculator'
  | 'selling_calculator'
  | 'decoupling_calculator';

export type SuperCalculatorInteraction =
  | 'tab_switch'
  | 'mode_switch'
  | 'history_save'
  | 'history_load'
  | 'example_load'
  | 'profile_save'
  | 'clear';

export function initAnalytics(): void {
  if (app || !firebaseConfig) return;
  try {
    app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
  } catch (error) {
    if (isDevelopment) {
      console.warn('Super calculator analytics init skipped', error);
    }
  }
}

function safeLogEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!analytics) return;
  try {
    firebaseLogEvent(analytics, name, params as Record<string, unknown>);
  } catch (error) {
    if (isDevelopment) {
      console.warn('Super calculator analytics logEvent failed', name, error);
    }
  }
}

export function setAnalyticsCea(ceaNumber: string | null | undefined): void {
  if (!analytics || !ceaNumber || !ceaNumber.trim()) return;
  try {
    setUserProperties(analytics, { cea_number: ceaNumber.trim() });
  } catch (error) {
    if (isDevelopment) {
      console.warn('Super calculator analytics setUserProperties failed', error);
    }
  }
}

export function logCalculatorViewed(calculator: SuperCalculatorKey, ceaNumber?: string | null): void {
  safeLogEvent('super_calculator_page_viewed', {
    calculator,
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

export function logCalculatorMenuClick(calculator: SuperCalculatorKey, ceaNumber?: string | null): void {
  safeLogEvent('super_calculator_menu_click', {
    calculator,
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

export function logPdfExport(
  calculator: SuperCalculatorKey,
  agent?: { CEANumber?: string | null; Name?: string | null } | null,
): void {
  safeLogEvent('super_calculator_pdf_export', {
    calculator,
    ...(agent?.CEANumber ? { cea_number: agent.CEANumber } : {}),
    ...(agent?.Name ? { agent_name: agent.Name } : {}),
  });
}

export function logCalculatorInteraction(
  calculator: SuperCalculatorKey,
  action: SuperCalculatorInteraction,
  params?: Record<string, string | number | boolean>,
): void {
  safeLogEvent('super_calculator_interaction', {
    calculator,
    action,
    ...(params ?? {}),
  });
}
